import { QueryClient } from '@tanstack/react-query';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { functions } from '@/lib/firebase';

// React Query client configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Query keys factory for consistent key management
export const queryKeys = {
  // Reviews
  reviews: {
    all: ['reviews'] as const,
    lists: () => [...queryKeys.reviews.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.reviews.lists(), filters] as const,
    details: () => [...queryKeys.reviews.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.reviews.details(), id] as const,
  },
  // Locations
  locations: {
    all: ['locations'] as const,
    lists: () => [...queryKeys.locations.all, 'list'] as const,
    list: (userId: string) => [...queryKeys.locations.lists(), userId] as const,
    details: () => [...queryKeys.locations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.locations.details(), id] as const,
  },
  // Responses
  responses: {
    all: ['responses'] as const,
    lists: () => [...queryKeys.responses.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.responses.lists(), filters] as const,
    details: () => [...queryKeys.responses.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.responses.details(), id] as const,
  },
  // Settings
  settings: {
    all: ['settings'] as const,
    detail: (userId: string) => [...queryKeys.settings.all, userId] as const,
  },
  // Billing
  billing: {
    all: ['billing'] as const,
    detail: (userId: string) => [...queryKeys.billing.all, userId] as const,
  },
  // Analytics
  analytics: {
    all: ['analytics'] as const,
    summary: (filters: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'summary', filters] as const,
  },
} as const;

// Typed wrapper for Firebase callable functions
export async function callFunction<TRequest = unknown, TResponse = unknown>(
  functionName: string,
  data?: TRequest
): Promise<TResponse> {
  try {
    const callable = httpsCallable<TRequest, TResponse>(functions, functionName);
    const result: HttpsCallableResult<TResponse> = await callable(data);
    return result.data;
  } catch (error) {
    console.error(`Error calling function ${functionName}:`, error);
    throw error;
  }
}

// API function wrappers for common operations
export const api = {
  // GBP OAuth
  gbp: {
    startOAuth: () => callFunction<void, { consentUrl: string }>('gbpOAuthStart'),
    getAccounts: () =>
      callFunction<void, { accounts: unknown[] }>('gbpGetAccounts'),
    getLocations: (accountId: string) =>
      callFunction<{ accountId: string }, { locations: unknown[] }>(
        'gbpGetLocations',
        { accountId }
      ),
  },

  // Reviews
  reviews: {
    syncReviews: (locationId: string) =>
      callFunction<{ locationId: string }, { success: boolean }>(
        'reviewsSync',
        { locationId }
      ),
    getSummary: () =>
      callFunction<void, { summary: unknown }>('reviewsGetSummary'),
  },

  // AI Responses
  responses: {
    generate: (reviewId: string) =>
      callFunction<{ reviewId: string }, { responseId: string }>(
        'responsesGenerate',
        { reviewId }
      ),
    post: (reviewId: string, responseText: string) =>
      callFunction<
        { reviewId: string; responseText: string },
        { success: boolean }
      >('responsesPost', { reviewId, responseText }),
  },

  // Billing
  billing: {
    createCheckoutSession: (priceId: string) =>
      callFunction<{ priceId: string }, { sessionUrl: string }>(
        'billingCreateCheckout',
        { priceId }
      ),
    createPortalSession: () =>
      callFunction<void, { portalUrl: string }>('billingCreatePortal'),
  },

  // Analytics
  analytics: {
    getSummary: (filters?: Record<string, unknown>) =>
      callFunction<
        { filters?: Record<string, unknown> },
        { summary: unknown }
      >('analyticsGetSummary', { filters }),
  },
};
